/**
 * `/policies/collection` — Task U15 (Tier-3 live-tenant readiness).
 *
 * The most sensitive configuration in the product, and until this screen the only
 * way to change it was a database client. `PosturePanel` said so in as many words —
 * *"wp_policies has no session endpoint in this build"* — which was true when Task 45
 * wrote it and stopped being true when G13 shipped `/api/v1/policies`. A screen that
 * tells an administrator a capability does not exist, while the API serves it, is
 * worse than no screen: it is a wrong answer nobody will check twice.
 *
 * ── The lifecycle is not this screen's ──────────────────────────────────────
 *
 * `useVersionedConfig` (Task U14, KTD13) owns list → create → patch → activate, the
 * touched-fields draft, the compare-and-set and the Skeleton / Empty / Retry /
 * Conflict states, and `VersionedConfigPanel` owns what each of them looks like. What
 * is here is this configuration's own words, its own fields, and the one thing no
 * other configuration screen has: a rollout, because a collection policy is the only
 * setting in this product whose effect is on **somebody else's machine**.
 *
 * ── Which row wears the active marker ───────────────────────────────────────
 *
 * `is_default && status === 'active'`, not `status === 'active'` alone. A company may
 * hold several active policies — assignment decides which machine gets which — but
 * `wp_policies_default_uk` is a partial unique index that permits at most one default,
 * and the panel promises that exactly one row wears the marker. The default is also
 * the honest base for the diff: it is the version a machine gets when nothing more
 * specific applies.
 *
 * ── Three fields that are deliberately absent ───────────────────────────────
 *
 *  - **`keylog_enabled`.** `wp_policies_privacy_ck` refuses `true` at the database and
 *    the API refuses it with a `400`. A control here could never succeed, and this
 *    codebase's rule is absent rather than disabled — a greyed switch asserts "this
 *    could do something, but not for you", and here nothing could, for anybody.
 *  - **The private lists and the redaction patterns.** They are on the wire and this
 *    build ships no editor for them, which the screen says out loud. Every redaction
 *    pattern is company-authored, runs on this server against an employee-chosen
 *    string, and is screened for catastrophic backtracking; the screen **fails
 *    closed**, so one bad character costs a whole company every window title it
 *    collects. A comma-splitting text box is how that happens, so there is not one.
 *  - **A second copy of the server's validation.** `idle_extended` must exceed
 *    `idle_short` and `screenshot_enabled` requires a recorded lawful basis; both are
 *    refused by the server with the field named, and the refusal is rendered
 *    verbatim through the panel's `writeError` rather than pre-empted here. This
 *    client keeps no second copy of the rules it would have to paraphrase from.
 *
 * ── One draft, two buttons, and that is a finding about the hook ────────────
 *
 * `useVersionedConfig` holds **one** `draft`, which is right for a screen that edits
 * one thing. This screen both creates a version and edits one, so the same typed
 * values feed both writes — pressing "create" makes a new draft carrying what is on
 * screen, and pressing "save" patches the selected version with it. That is a
 * defensible reading and it is not a free one: there is no way to have a half-typed
 * new-version name that does not also become part of the next `PATCH` body. It is
 * written down in the U15 report rather than worked around here.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { useSession } from '../../auth/SessionProvider';
import {
  POLICIES_PATH,
  activatePolicyPath,
  policyPath,
  type Policy,
  type PolicyCreateRequest,
  type PolicyDraft,
  type PolicyListResponse,
} from '../../api/policies';
import { VersionedConfigPanel } from '../../config/VersionedConfigPanel';
import { useVersionedConfig } from '../../config/useVersionedConfig';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { RolloutPanel } from './RolloutPanel';

/**
 * The three levels, as a closed list with an exhaustive label map — a fourth level
 * added by a later migration is a compile error here rather than a radio button with
 * no words next to it.
 */
const LEVELS = [1, 2, 3] as const;

const LEVEL_LABEL: Readonly<Record<(typeof LEVELS)[number], MessageKey>> = {
  1: 'policyEditor.level.1',
  2: 'policyEditor.level.2',
  3: 'policyEditor.level.3',
};

export function PolicyEditorScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  /** Which version the form is pointed at. `null` means "the one in force". */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const state = useVersionedConfig<PolicyListResponse, Policy, PolicyDraft, PolicyCreateRequest>({
    listPath: POLICIES_PATH,
    versions: (list) => list.data,
    idOf: (policy) => policy.id,
    labelOf: (policy) => `${policy.name} · ${policy.policy_version}`,
    isActive: (policy) => policy.status === 'active' && policy.is_default,
    fieldsOf: (policy) => ({
      // Keys are joined to draft keys by string equality and nothing types that
      // correspondence, so every key here is spelled as the draft spells it.
      name: policy.name,
      collection_level: String(policy.collection_level),
      idle_short_threshold_seconds: String(policy.idle_short_threshold_seconds),
      idle_extended_threshold_seconds: String(policy.idle_extended_threshold_seconds),
      collect_window_title: String(policy.collect_window_title),
      collect_off_hours: String(policy.collect_off_hours),
      screenshot_enabled: String(policy.screenshot_enabled),
      screenshot_lawful_basis: policy.screenshot_lawful_basis ?? '',
      usb_monitoring_enabled: String(policy.usb_monitoring_enabled),
      file_monitoring_enabled: String(policy.file_monitoring_enabled),
      // Rendered but not editable, which `fieldsOf` explicitly allows: the status of
      // a row is what tells a reader why it has an activate button and no other row
      // does.
      status: policy.status,
    }),
    /*
      The token the list row now carries — U15's backend half. Before it, this
      function had nothing to return but `null` and every write on this surface was
      refused here rather than sent.

      Guarded on **falsiness**, not on `=== ''` (review round 1). The contract makes
      `etag` required and the type says `string`, but a response that omitted it would
      have produced `{ kind: 'if-match', token: undefined }` — non-null, therefore
      `row.writable`, therefore a write going out as `If-Match: "undefined"` to be
      refused `400` by the server. `web/src/api/policies.ts` argues that the hook
      refuses a write when the token is missing; this is what makes that true.
    */
    preconditionOf: (policy) => (policy.etag ? { kind: 'if-match', token: policy.etag } : null),
    createPath: POLICIES_PATH,
    createBody: (draft) => ({
      name: (draft.name ?? '').trim(),
      ...(draft.collection_level === undefined ? {} : { collection_level: draft.collection_level }),
      ...(draft.idle_short_threshold_seconds === undefined
        ? {}
        : { idle_short_threshold_seconds: draft.idle_short_threshold_seconds }),
      ...(draft.idle_extended_threshold_seconds === undefined
        ? {}
        : { idle_extended_threshold_seconds: draft.idle_extended_threshold_seconds }),
      ...(draft.collect_window_title === undefined
        ? {}
        : { collect_window_title: draft.collect_window_title }),
      ...(draft.collect_off_hours === undefined
        ? {}
        : { collect_off_hours: draft.collect_off_hours }),
      ...(draft.screenshot_enabled === undefined
        ? {}
        : { screenshot_enabled: draft.screenshot_enabled }),
      ...(draft.screenshot_lawful_basis === undefined
        ? {}
        : { screenshot_lawful_basis: draft.screenshot_lawful_basis }),
      ...(draft.usb_monitoring_enabled === undefined
        ? {}
        : { usb_monitoring_enabled: draft.usb_monitoring_enabled }),
      ...(draft.file_monitoring_enabled === undefined
        ? {}
        : { file_monitoring_enabled: draft.file_monitoring_enabled }),
    }),
    patchPath: (policy) => policyPath(policy.id),
    activation: { kind: 'endpoint', path: (policy) => activatePolicyPath(policy.id) },
  });

  /**
   * The version the form edits: the one chosen from the table, else the one in
   * force, else the first row. Never invented — when the list is empty this is
   * `null` and the form is not rendered at all.
   */
  const target: Policy | null =
    state.versions.find((policy) => policy.id === selectedId) ?? state.active ?? null;

  /** The value on screen: what was typed, else what the target version holds. */
  const shown = <K extends keyof PolicyDraft>(key: K, fallback: PolicyDraft[K]): PolicyDraft[K] =>
    state.draft[key] ?? fallback;

  const newName = (state.draft.name ?? '').trim();
  const canPatchTarget = target !== null && state.canWrite(target) && state.touched.length > 0;

  const createAction = (
    <div className="wp-field" data-testid="u15-create">
      <label className="wp-field__label" htmlFor="u15-create-name">
        {t('policyEditor.create.name')}
      </label>
      <input
        className="wp-field__input"
        id="u15-create-name"
        data-testid="u15-create-name"
        type="text"
        maxLength={120}
        value={state.draft.name ?? ''}
        onChange={(event) => {
          state.setField('name', event.target.value);
        }}
      />
      {/* Absent, not disabled: with no name there is no request to make, and the
          server would answer `400` naming the field the person has not filled in. */}
      {newName === '' ? (
        <p className="wp-small wp-muted" data-testid="u15-create-hint">
          {t('policyEditor.create.hint')}
        </p>
      ) : (
        <button
          type="button"
          className="wp-button wp-button--primary"
          data-testid="u15-create-submit"
          onClick={() => {
            state.create();
          }}
        >
          {t('policyEditor.create.submit')}
        </button>
      )}
    </div>
  );

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('policyEditor.title')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('policyEditor.intro')}</p>

      <VersionedConfigPanel
        {...state}
        title={t('policyEditor.panel.title')}
        subject={t('policyEditor.subject')}
        emptyTitle={t('policyEditor.empty.title')}
        emptyDescription={t('policyEditor.empty.body')}
        createAction={createAction}
        testId="u15-config"
        rowAction={(row) => (
          <div className="wp-filters__actions">
            <button
              type="button"
              className="wp-button"
              data-testid={`u15-select-${row.id}`}
              onClick={() => {
                setSelectedId(row.id);
                // A different version is a different decision: the draft was typed
                // against the row that was on screen, and carrying it over would
                // silently apply somebody's numbers to a policy they were not
                // looking at.
                state.resetDraft();
              }}
            >
              {t('policyEditor.row.edit')}
            </button>
            {row.fields.status === 'draft' ? (
              <button
                type="button"
                className="wp-button"
                data-testid={`u15-activate-${row.id}`}
                onClick={() => {
                  const version = state.versions.find((policy) => policy.id === row.id);
                  if (version !== undefined) state.activate(version);
                }}
              >
                {t('policyEditor.row.activate')}
              </button>
            ) : null}
          </div>
        )}
      />

      {/*
        Review round 1, Major 1 — and it is a finding about U14's hook before it is a
        finding about this screen. `activate` sends `bodyWith({}, precondition)`, which
        for an `if-match` surface is exactly `{}`, so `make_default` never reaches
        `POST /policies/{id}/activate` and `activatePolicy` defaults it to `false`
        (`admin-service.ts`). `resolveEffectivePolicy` reaches a policy only through an
        assignment or the company default, so a version activated from here is
        published and governs nobody.

        The button stays, because it does something real: it publishes the draft, which
        is the only transition into `active` the API has, and it is a decision a second
        person makes after the draft was written. What is added is the sentence saying
        what it does NOT do. There is deliberately no greyed "make default" control
        beside it: the shared lifecycle has no activation-body channel, so there is no
        request such a control could send — absent, not disabled, which is this
        codebase's rule and the same one applied to the keylog switch below.
      */}
      <p className="wp-small wp-muted" data-testid="u15-activate-note">
        {t('policyEditor.activate.note')}
      </p>

      {target === null ? null : (
        <section
          className="wp-section"
          aria-labelledby="u15-form-heading"
          data-testid="u15-form"
          key={target.id}
        >
          <h2 className="wp-section__title" id="u15-form-heading">
            {t('policyEditor.form.title', { name: target.name, version: target.policy_version })}
          </h2>
          <p className="wp-small wp-muted" data-testid="u15-form-target">
            {target.status === 'active'
              ? t('policyEditor.form.active')
              : t('policyEditor.form.draft')}
          </p>

          <fieldset className="wp-filters__group" data-testid="u15-level">
            <legend>{t('policyEditor.level.legend')}</legend>
            {LEVELS.map((level) => (
              <label key={level}>
                <input
                  type="radio"
                  name="u15-level"
                  data-testid={`u15-level-${String(level)}`}
                  checked={shown('collection_level', target.collection_level) === level}
                  onChange={() => {
                    state.setField('collection_level', level);
                  }}
                />
                <span>{t(LEVEL_LABEL[level])}</span>
              </label>
            ))}
          </fieldset>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u15-idle-short">
              {t('policyEditor.idleShort')}
            </label>
            <input
              className="wp-field__input"
              id="u15-idle-short"
              data-testid="u15-idle-short"
              type="number"
              min={30}
              max={86400}
              value={shown('idle_short_threshold_seconds', target.idle_short_threshold_seconds)}
              onChange={(event) => {
                state.setField('idle_short_threshold_seconds', Number(event.target.value));
              }}
            />
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u15-idle-extended">
              {t('policyEditor.idleExtended')}
            </label>
            <input
              className="wp-field__input"
              id="u15-idle-extended"
              data-testid="u15-idle-extended"
              type="number"
              min={60}
              max={86400}
              value={shown(
                'idle_extended_threshold_seconds',
                target.idle_extended_threshold_seconds,
              )}
              onChange={(event) => {
                state.setField('idle_extended_threshold_seconds', Number(event.target.value));
              }}
            />
            <p className="wp-small wp-muted">{t('policyEditor.idle.hint')}</p>
          </div>

          <div className="wp-filters__group">
            <label className="wp-toggle" htmlFor="u15-window-title">
              <input
                id="u15-window-title"
                type="checkbox"
                data-testid="u15-window-title"
                checked={shown('collect_window_title', target.collect_window_title)}
                onChange={(event) => {
                  state.setField('collect_window_title', event.target.checked);
                }}
              />
              <span>{t('policyEditor.windowTitle')}</span>
            </label>
            <label className="wp-toggle" htmlFor="u15-off-hours">
              <input
                id="u15-off-hours"
                type="checkbox"
                data-testid="u15-off-hours"
                checked={shown('collect_off_hours', target.collect_off_hours)}
                onChange={(event) => {
                  state.setField('collect_off_hours', event.target.checked);
                }}
              />
              <span>{t('policyEditor.offHours')}</span>
            </label>
            <label className="wp-toggle" htmlFor="u15-usb">
              <input
                id="u15-usb"
                type="checkbox"
                data-testid="u15-usb"
                checked={shown('usb_monitoring_enabled', target.usb_monitoring_enabled)}
                onChange={(event) => {
                  state.setField('usb_monitoring_enabled', event.target.checked);
                }}
              />
              <span>{t('policyEditor.usb')}</span>
            </label>
            <label className="wp-toggle" htmlFor="u15-file">
              <input
                id="u15-file"
                type="checkbox"
                data-testid="u15-file"
                checked={shown('file_monitoring_enabled', target.file_monitoring_enabled)}
                onChange={(event) => {
                  state.setField('file_monitoring_enabled', event.target.checked);
                }}
              />
              <span>{t('policyEditor.file')}</span>
            </label>
          </div>

          <div className="wp-filters__group" data-testid="u15-screenshot">
            <label className="wp-toggle" htmlFor="u15-screenshot-enabled">
              <input
                id="u15-screenshot-enabled"
                type="checkbox"
                data-testid="u15-screenshot-enabled"
                checked={shown('screenshot_enabled', target.screenshot_enabled)}
                onChange={(event) => {
                  state.setField('screenshot_enabled', event.target.checked);
                }}
              />
              <span>{t('policyEditor.screenshot')}</span>
            </label>
            <div className="wp-field">
              <label className="wp-field__label" htmlFor="u15-basis">
                {t('policyEditor.basis')}
              </label>
              <textarea
                className="wp-field__input"
                id="u15-basis"
                data-testid="u15-basis"
                maxLength={1000}
                rows={2}
                value={shown('screenshot_lawful_basis', target.screenshot_lawful_basis ?? '') ?? ''}
                onChange={(event) => {
                  state.setField('screenshot_lawful_basis', event.target.value);
                }}
              />
              <p className="wp-small wp-muted">{t('policyEditor.basis.hint')}</p>
            </div>
          </div>

          {/* The fields this build does not offer, named rather than omitted — the
              same decision PosturePanel makes about the values it will not invent. */}
          <p className="wp-small wp-muted" data-testid="u15-no-list-editor">
            {t('policyEditor.noListEditor')}
          </p>
          <p className="wp-small wp-muted" data-testid="u15-no-keylog">
            {t('policyEditor.noKeylog')}
          </p>

          <div className="wp-filters__actions">
            {canPatchTarget ? (
              <button
                type="button"
                className="wp-button wp-button--primary"
                data-testid="u15-save"
                onClick={() => {
                  state.patch(target);
                }}
              >
                {t('policyEditor.save')}
              </button>
            ) : (
              <p className="wp-small wp-muted" data-testid="u15-save-absent">
                {state.touched.length === 0
                  ? t('policyEditor.save.nothing')
                  : t('policyEditor.save.blocked')}
              </p>
            )}
            {state.touched.length === 0 ? null : (
              <button
                type="button"
                className="wp-button"
                data-testid="u15-discard"
                onClick={() => {
                  state.resetDraft();
                }}
              >
                {t('policyEditor.discard')}
              </button>
            )}
          </div>
        </section>
      )}

      {/*
        The rollout reads the version **in force**, not the one being edited: "how
        far has this reached the fleet" is a question about the policy machines are
        actually running, and pointing it at a draft nobody has been served would
        answer with four zeroes that mean nothing.

        `canSeeDevices` hides the per-device list, and hiding it is a courtesy rather
        than a control — `nav/menu.ts`'s own note applies here too. The server refuses
        the read on `workforce.manage_devices` whatever this believes, and the panel
        renders that refusal rather than swallowing it.
      */}
      <RolloutPanel
        policy={state.active}
        canSeeDevices={user?.permissions.includes('workforce.manage_devices') === true}
        locale={locale}
        timeZone={user?.timezone ?? 'Asia/Ho_Chi_Minh'}
      />
    </div>
  );
}
